/**
 * Feature-flag resolution for the AI search surface (Search Graft Track A /
 * the API-versioning plan, ADO 289472).
 *
 * The source of truth is now the AiPoweredSearchV1 BUILDER flag
 * (`builderInfo.flags.aiPoweredSearchV1`, serialized from the server enum):
 * it selects the server generation (v1 legacy vs v2 graft — see
 * getSearchApiVersion) AND provides the default for the client rendering
 * contract below. History: aiSearchStableList began as a pure client-side
 * `BTLocalStorage` flag because Track A shipped rendering changes ahead of
 * any server enum change; the builder flag has since landed, so the builder
 * is the default and localStorage remains only as the support/dev override.
 *
 * Resolution order for aiSearchStableList:
 *   1. explicit localStorage override (support can flip a single user's browser
 *      from the console: `localStorage["bt-boolean-aiSearchStableList"] = "false"`)
 *   2. the AiPoweredSearchV1 builder flag — ON follows the flag (the stable
 *      list is part of the graft experience), OFF means the legacy
 *      progressive-paint contract, matching origin/master.
 */
import type { BuilderInfo } from "helpers/AppProvider.types";

import { BTLocalStorage } from "types/btStorage";

/**
 * Which server generation the AI search surface talks to. Selected by the
 * AiPoweredSearchV1 builder flag (ADO 289472, the API-versioning plan):
 * the legacy search service is vendored untouched under /api/v1/* and the
 * graft stack lives under /api/v2/*, so "v1 flag OFF" means the v1 (legacy)
 * server and today's shipped UX, and "v1 flag ON" means the v2 (graft)
 * server and the new experience.
 */
export type SearchApiVersion = "v1" | "v2";

/**
 * ADO 289472: the AiPoweredSearchV1 builder flag. OFF (the default) pins the
 * AiSearch surface to origin/master behavior — POST {base}v1/search,
 * progressive-paint rendering, and NONE of the graft-only sidecar calls
 * (suggest typeahead, record panel, agent dock), which the legacy server
 * does not serve. ON selects the graft experience against /v2/*.
 *
 * Read from the builder-flags object the caller already has (the
 * BuilderInfoContext pattern WhenBuilderFlag uses) — commonComponents may
 * not read builder flags from context directly, so the parent passes
 * builderInfo down and pure helpers derive from it.
 */
export function isAiPoweredSearchV1Enabled(builderInfo: BuilderInfo | null | undefined): boolean {
    return builderInfo?.flags?.aiPoweredSearchV1 === true;
}

/** The endpoint prefix derived from the AiPoweredSearchV1 flag. */
export function getSearchApiVersion(builderInfo: BuilderInfo | null | undefined): SearchApiVersion {
    return isAiPoweredSearchV1Enabled(builderInfo) ? "v2" : "v1";
}

/**
 * PR-A1 (ADO 286301): buffered commit-once rendering for the AI search list.
 *
 * ON  — result events buffer invisibly; the fused list commits to visible state
 *       exactly once (at `done`, or when a 600ms stability window elapses with
 *       all launched ops complete); late ops append below the committed list;
 *       skeletons show until the first commit.
 * OFF — legacy behavior: every result event re-fuses and repaints the list
 *       (the 4-8 visible reshuffles pinned by the T4 baseline suite).
 *
 * DEFAULT: the AiPoweredSearchV1 builder flag (ADO 289472). The stable list
 * is part of the graft (v2) experience, so it defaults ON exactly when the
 * builder is on the v2 server, and OFF — the origin/master progressive-paint
 * contract — when the builder is on the vendored v1 legacy server. (Interim
 * history: graft 2026-08-28 shipped the stable list default-ON in every
 * build mode while it was still a client-only flag; the builder flag now
 * carries that rollout per builder.) The localStorage override above remains
 * the per-browser escape hatch in BOTH directions: `false` falls back to the
 * legacy path on a v2 builder (behavior pinned by the T4 baseline suites),
 * `true` previews the stable list on a v1 builder.
 */
export function isAiSearchStableListEnabled(builderInfo: BuilderInfo | null | undefined): boolean {
    const override = BTLocalStorage.get("bt-boolean-aiSearchStableList");
    if (override !== null && override !== undefined) return override;
    return isAiPoweredSearchV1Enabled(builderInfo);
}

/**
 * graft/A7 (Rapid QA): the streamed AI overview in the reserved QA slot.
 *
 * ON  - after a query commits on a v2 (graft) builder, the client opens
 *       POST /api/v2/answer (SSE) and renders facts, then streamed prose,
 *       INSIDE the fixed-height QA slot. The server's RAPID_QA_ENABLED is the
 *       master switch: off => 404 => the client goes inert for the session,
 *       so this flag only ever narrows. On a v1 (legacy) builder the surface
 *       never calls the endpoint at all (AiSearch.tsx passes an empty base url).
 * OFF - the slot keeps today's behavior (legacy qa event only).
 *
 * DEFAULT: ON. Per-browser kill switch for support/dev:
 *   localStorage["bt-boolean-aiSearchRapidQa"] = "false"
 */
export function isAiSearchRapidQaEnabled(): boolean {
    const override = BTLocalStorage.get("bt-boolean-aiSearchRapidQa");
    if (override !== null && override !== undefined) return override;
    return true;
}
